import {
  Plus,
  Search,
  MoreVertical,
  Edit2,
  Settings,
  Trash2,
} from "lucide-react";

// Wizard Step 1 — browse/search existing roles and launch the role form.
// State is owned by the parent UserRoleManagement; this is a presentational
// component that receives every handler/value it needs as props.
export default function RoleListView({
  roles,
  searchRoles,
  setSearchRoles,
  showSystemRoles,
  setShowSystemRoles,
  openRoleMenuId,
  setOpenRoleMenuId,
  roleMenuRef,
  onAddRole,
  onEditRole,
  onDeactivateRole,
  onReactivateRole,
  onDeleteRole,
  canCreateRole,
  canEditRole,
  canDeleteRole,
}) {
  const matchesSearch = (role) =>
    !searchRoles ||
    role.name?.toLowerCase().includes(searchRoles.toLowerCase()) ||
    role.description?.toLowerCase().includes(searchRoles.toLowerCase());

  const visibleRoles = roles
    .filter((r) => r.type === "custom" || (r.type === "system" && showSystemRoles))
    .filter(matchesSearch);
  const systemRoles = roles.filter((r) => r.type === "system");
  const activeCount = visibleRoles.filter((role) => role.active).length;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex flex-shrink-0 items-center justify-between gap-4 border-b border-gray-300 bg-white px-4 py-4">
        <div className="flex items-center gap-3">
          <span className="rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-blue-700">
            {visibleRoles.length} Saved
          </span>
          <span className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-emerald-700">
            {activeCount} Active
          </span>
          <button
            onClick={() => setShowSystemRoles(!showSystemRoles)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
          >
            {showSystemRoles ? "Hide System Roles" : "Show System Roles"}
          </button>
        </div>
        <button
          onClick={onAddRole}
          disabled={!canCreateRole}
          className={`inline-flex items-center gap-2 rounded-lg bg-redhalo-red px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 ${!canCreateRole ? "cursor-not-allowed opacity-50" : ""}`}
        >
          <Plus className="w-4 h-4" />
          <span>Add Role</span>
        </button>
      </div>

      {showSystemRoles && (
        <div className="border-b border-gray-300 bg-gray-50 px-4 py-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
            System Role Badges
          </p>
          <div className="flex flex-wrap gap-2">
            {systemRoles.map((role) => (
                <span
                  key={role.id}
                  className="rounded-full border border-purple-200 bg-purple-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-purple-700"
                >
                  {role.name}
                </span>
              ))}
          </div>
        </div>
      )}

      <div className="flex-1 overflow-hidden p-4">
        <div className="rounded-t-table-lg overflow-hidden border border-gray-200 bg-[#fafafa] shadow-card h-full flex flex-col">
          <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 bg-white px-4 py-3">
            <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-gray-800">
              Role Grid
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold tracking-wider text-gray-600">
                TABLE
              </span>
            </h3>
            <div className="relative min-w-[220px] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                placeholder="Search roles..."
                value={searchRoles}
                onChange={(e) => setSearchRoles(e.target.value)}
                className="w-full max-w-md rounded-full border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300/80"
              />
            </div>
            <div className="ml-auto rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-700">
              Total: {visibleRoles.length}
            </div>
          </div>

          {visibleRoles.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-gray-500">No roles found</p>
          ) : (
            <div className="h-full overflow-auto">
              <table className="min-w-full table-fixed">
                <thead className="bg-gray-200/80 text-xs font-bold uppercase tracking-wide text-gray-700">
                  <tr>
                    <th className="w-[28%] px-4 py-3 text-left">Role</th>
                    <th className="w-[36%] px-4 py-3 text-left">Description</th>
                    <th className="w-[14%] px-4 py-3 text-left">Status</th>
                    <th className="w-[12%] px-4 py-3 text-left">Type</th>
                    <th className="w-[10%] px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRoles.map((role, idx) => (
                    <RoleTableRow
                      key={role.id}
                      role={role}
                      rowIndex={idx}
                      openRoleMenuId={openRoleMenuId}
                      setOpenRoleMenuId={setOpenRoleMenuId}
                      roleMenuRef={roleMenuRef}
                      onEditRole={onEditRole}
                      onDeactivateRole={onDeactivateRole}
                      onReactivateRole={onReactivateRole}
                      onDeleteRole={onDeleteRole}
                      canEditRole={canEditRole}
                  canDeleteRole={canDeleteRole}
                />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-300 bg-gray-200/80 px-4 py-3">
            <p className="text-sm text-gray-600">
              Showing {visibleRoles.length} saved role{visibleRoles.length === 1 ? "" : "s"}
            </p>
            <div className="flex items-center gap-2">
              <span className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700">
                Grid View
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RoleTableRow({
  role,
  rowIndex,
  openRoleMenuId,
  setOpenRoleMenuId,
  roleMenuRef,
  onEditRole,
  onDeactivateRole,
  onReactivateRole,
  onDeleteRole,
  canEditRole,
  canDeleteRole,
}) {
  return (
    <tr
      className={`border-b border-gray-300 ${
        rowIndex % 2 === 0 ? "bg-[#fafafa]" : "bg-gray-100/80"
      }`}
    >
      <td className="px-4 py-3 align-top">
        <div className="space-y-1">
          <p className="text-sm font-semibold text-gray-900">{role.name}</p>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-block rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                role.active
                  ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "bg-gray-200 text-gray-700"
              }`}
            >
              {role.active ? "Active" : "Inactive"}
            </span>
            {role.deletable === false && (
              <span className="inline-block rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                Protected
              </span>
            )}
            {role.type === "system" && (
              <span className="inline-block rounded-md border border-purple-200 bg-purple-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-purple-700">
                Default
              </span>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 align-top">
        {role.description ? (
          <p className="line-clamp-2 text-xs text-gray-600">{role.description}</p>
        ) : (
          <span className="text-xs italic text-gray-400">No description</span>
        )}
      </td>
      <td className="px-4 py-3 align-top">
        <span
          className={`inline-block rounded-md px-2.5 py-1 text-xs font-semibold ${
            role.active
              ? "bg-emerald-100 text-emerald-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {role.active ? "Enabled" : "Disabled"}
        </span>
      </td>
      <td className="px-4 py-3 align-top">
        <span
          className={`inline-block rounded-md px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${
            role.type === "system"
              ? "bg-purple-100 text-purple-700"
              : "bg-blue-100 text-blue-700"
          }`}
        >
          {role.type === "system" ? "System" : "Custom"}
        </span>
      </td>
      <td className="px-4 py-3 align-top text-right">
        <div className="relative inline-block" ref={openRoleMenuId === role.id ? roleMenuRef : null}>
          <button
            onClick={() => setOpenRoleMenuId(openRoleMenuId === role.id ? null : role.id)}
            className="rounded-md p-1.5 text-gray-600 transition-colors hover:bg-gray-200/70 hover:text-gray-800"
          >
            <MoreVertical className="w-5 h-5" />
          </button>
          {openRoleMenuId === role.id && (
            <div className="absolute right-0 top-full z-50 mt-1 w-40 rounded-lg border border-gray-200 bg-white py-2 shadow-lg">
              <button
                onClick={() => {
                  if (!canEditRole) return;
                  onEditRole(role);
                  setOpenRoleMenuId(null);
                }}
                disabled={!canEditRole}
                className={`flex w-full items-center space-x-2 px-3 py-2 text-left text-sm ${canEditRole ? "hover:bg-gray-100" : "cursor-not-allowed opacity-50"}`}
              >
                <Edit2 className="w-4 h-4" />
                <span>Edit</span>
              </button>

              {role.active ? (
                <button
                  onClick={() => {
                    if (!canEditRole) return;
                    onDeactivateRole(role.id);
                    setOpenRoleMenuId(null);
                  }}
                  disabled={!canEditRole}
                  className={`flex w-full items-center space-x-2 px-3 py-2 text-left text-sm text-orange-600 ${canEditRole ? "hover:bg-gray-100" : "cursor-not-allowed opacity-50"}`}
                >
                  <Settings className="w-4 h-4" />
                  <span>Deactivate</span>
                </button>
              ) : (
                <button
                  onClick={() => {
                    if (!canEditRole) return;
                    onReactivateRole(role.id);
                    setOpenRoleMenuId(null);
                  }}
                  disabled={!canEditRole}
                  className={`flex w-full items-center space-x-2 px-3 py-2 text-left text-sm text-emerald-700 ${canEditRole ? "hover:bg-gray-100" : "cursor-not-allowed opacity-50"}`}
                >
                  <Settings className="h-4 w-4" />
                  <span>Reactivate</span>
                </button>
              )}
              {role.deletable !== false && (
                <button
                  onClick={() => {
                    if (!canDeleteRole) return;
                    onDeleteRole(role.id);
                    setOpenRoleMenuId(null);
                  }}
                  disabled={!canDeleteRole}
                  className={`flex w-full items-center space-x-2 px-3 py-2 text-left text-sm text-red-600 ${canDeleteRole ? "hover:bg-gray-100" : "cursor-not-allowed opacity-50"}`}
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete</span>
                </button>
              )}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}
